import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@platform/api";
import {
  createServerSideDatasource,
  DataTableProps,
  FetchPageParams,
  useAgGridOptions,
} from "@platform/design-system";

import { extractErrorMessage } from "../utils";
import { analystSortKeyMapping } from "../pages/RFPs/utils";
import RfpStatus from "../pages/RFPDetails/components/rfpStatus";

import { fetchRfps, getRfpsQueryKey, prefetchRfps } from "./useRfps";
import { RfpsBaseParams } from "./useRfpsList";

type AnalystRfpsDataProps = {
  debouncedSearch: string;
  statusFilters: string[];
  tabIndex: number;
};

type AnalystRfpsDataReturn = {
  tabCounts: {
    active: number;
    delivered: number;
  };
  dataTableOptions: DataTableProps;
  resetPage: VoidFunction;
};

const DEFAULT_PAGE_SIZE = 10;
const statusField = "details.status";

export function useAnalystRfpsData({
  debouncedSearch,
  statusFilters,
  tabIndex,
}: AnalystRfpsDataProps): AnalystRfpsDataReturn {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);
  const [tabCounts, setTabCounts] = useState<{
    active: number;
    delivered: number;
  }>({
    active: 0,
    delivered: 0,
  });

  const queryClient = useQueryClient();
  const axiosInstance = useApi();

  const { resetPage, ...dataTableOptions } = useAgGridOptions({
    sessionStorageKey: "analystRfpsPageState",
    colId: statusField,
    sort: "asc",
  });

  const fetchData = useCallback(
    async ({ page, pageSize, sortModel }: FetchPageParams) => {
      try {
        const sortKey =
          (sortModel?.[0]?.sort === "desc" ? "-" : "") +
          analystSortKeyMapping[sortModel?.[0]?.colId || statusField];

        const currentTabStatusFilters =
          tabIndex === 0
            ? statusFilters
            : ["deliveredFinal" as keyof typeof RfpStatus];
        const otherTabStatusFilters =
          tabIndex === 0
            ? ["deliveredFinal" as keyof typeof RfpStatus]
            : statusFilters;

        const params: RfpsBaseParams = {
          query: debouncedSearch,
          page,
          limit: pageSize,
          sortKey,
          statusFilters: currentTabStatusFilters,
        };
        const results = await queryClient.fetchQuery({
          queryKey: getRfpsQueryKey(params),
          queryFn: () => fetchRfps(axiosInstance, params),
          staleTime: 5000,
        });

        const prefetchParams: RfpsBaseParams = {
          page: 0,
          limit: pageSize,
          statusFilters: otherTabStatusFilters,
          query: "",
          sortKey: statusField,
        };

        prefetchRfps(queryClient, axiosInstance, prefetchParams);
        const { totalResults } = await queryClient.fetchQuery({
          queryKey: getRfpsQueryKey(prefetchParams),
          queryFn: () =>
            fetchRfps(axiosInstance, { ...prefetchParams, limit: 1 }),
        });
        setTabCounts({
          active: tabIndex === 0 ? results.totalResults : totalResults,
          delivered: tabIndex === 1 ? results.totalResults : totalResults,
        });

        return { rows: results.results, total: results.totalResults };
      } catch (err) {
        setError(extractErrorMessage(err, "RFP list couldn't be retrieved"));
        throw err;
      }
    },
    [axiosInstance, debouncedSearch, queryClient, statusFilters, tabIndex],
  );

  const serverSideDatasource = useMemo(
    () => createServerSideDatasource(fetchData),
    [fetchData],
  );

  return {
    tabCounts,
    resetPage,
    dataTableOptions: {
      ...dataTableOptions,
      serverSideDatasource,
      rowModelType: "serverSide",
      serverSideInitialRowCount: DEFAULT_PAGE_SIZE,
      cacheBlockSize: 20,
      overlayNoRowsTemplate: `No ${tabIndex === 0 ? "Active" : "Delivered"} Open RFP Projects assigned to you.`,
    },
  };
}
